/**
 * Shared plumbing for the T5 screens: client injection (production BFF client by default, fixture client in tests),
 * a small async loader with the five screen states (loading / ready / empty is the caller's / error / flag-off /
 * forbidden), the advanced-audit sub navigation and role helpers.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { EmptyState, ErrorState, Loading, PageHeader } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import { useFlags, type FlagState } from '../../../../../lib/flags';
import { useWorkspaceName } from '../../../_shared/ListScreen';
import { bffRemediationClient, failureOf, type LoadFailure, type RemediationClient } from './client';

export const AUDIT_FLAGS = {
  remediation: 'audit.remediation', monitoring: 'audit.monitoring', securityDeep: 'audit.security_deep', expert: 'audit.expert',
} as const;
export const providerFlag = (p: string) => `audit.remediation.${p}`;

const ClientCtx = createContext<RemediationClient | null>(null);
let defaultClient: RemediationClient | null = null;

/** Wrap a subtree to inject a client (tests, previews). Without it the BFF client is used. */
export function RemediationClientProvider({ client, children }: { client: RemediationClient; children: ReactNode }) {
  return <ClientCtx.Provider value={client}>{children}</ClientCtx.Provider>;
}
export function useRemediationClient(): RemediationClient {
  const c = useContext(ClientCtx);
  return c ?? (defaultClient ??= bffRemediationClient());
}

export type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'failed'; failure: LoadFailure };

/** Run `fn` with the client on mount and whenever `key` changes. `reload` re-runs it; `set` patches local data. */
export function useLoad<T>(key: string | null, fn: (c: RemediationClient) => Promise<T>): Load<T> & { reload: () => void; set: (d: T) => void } {
  const client = useRemediationClient();
  const [state, setState] = useState<Load<T>>({ status: 'loading' });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (key === null) return;
    let live = true;
    fn(client).then(
      (data) => { if (live) setState({ status: 'ready', data }); },
      (e: unknown) => { if (live) setState({ status: 'failed', failure: failureOf(e) }); },
    );
    return () => { live = false; };
    // fn is intentionally not a dependency: callers pass inline lambdas keyed by `key`
  }, [client, key, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  const set = useCallback((data: T) => setState({ status: 'ready', data }), []);
  return { ...state, reload, set };
}

/** Loading / failure renderer shared by every screen; `null` when the caller should render its data. */
export function LoadState({ load, notFoundKey }: { load: Load<unknown>; notFoundKey?: string }) {
  const { t } = useApp();
  if (load.status === 'loading') return <Loading label={t('common.loading')} />;
  if (load.status === 'ready') return null;
  return <FailureState failure={load.failure} notFoundKey={notFoundKey} />;
}

export function FailureState({ failure, notFoundKey }: { failure: LoadFailure; notFoundKey?: string }) {
  const { t } = useApp();
  if (failure === 'flag_off') return <EmptyState title={t('audit-remediation.actions.state.flagOff')}><p>{t('audit-remediation.actions.state.flagOffBody')}</p></EmptyState>;
  if (failure === 'forbidden') return <EmptyState title={t('audit-remediation.actions.state.forbidden')}><p>{t('audit-remediation.actions.state.forbiddenBody')}</p></EmptyState>;
  if (failure === 'not_found') return <EmptyState title={t(notFoundKey ?? 'audit-remediation.actions.state.notFound')} />;
  return <ErrorState message={t('audit-remediation.actions.state.error')} />;
}

/** Customer roles of the signed-in user in the active workspace. */
export function useRoles() {
  const { me } = useApp();
  return useMemo(() => {
    const roles = me?.roles ?? [];
    const owner = roles.includes('customer_owner');
    const admin = roles.includes('customer_admin');
    return { owner, admin, manage: owner || admin, write: owner || admin || roles.includes('customer_member') };
  }, [me]);
}

const TABS = [
  { to: '/audit/advanced/actions', key: 'actions' },
  { to: '/audit/advanced/integrations', key: 'integrations' },
  { to: '/audit/advanced/monitoring', key: 'monitoring' },
  { to: '/audit/advanced/security', key: 'security' },
] as const;

/** Page frame: editorial header + sub navigation + flag gate. Children render only when the flag is on. */
export function AdvancedPage({ area, flag, flagOverride, children }: {
  area: 'actions' | 'integrations' | 'monitoring' | 'security'; flag: string; flagOverride?: FlagState; children: ReactNode;
}) {
  const { t } = useApp();
  const flags = useFlags();
  const workspace = useWorkspaceName();
  const state = flagOverride ?? flags(flag);
  const job = useJobParam();
  const qs = job ? `?job=${encodeURIComponent(job)}` : '';
  return (
    <section className="qm-page ar-page" aria-labelledby="page-title">
      <PageHeader eyebrow={workspace} title={t(`audit-remediation.${area}.title`)} intro={t(`audit-remediation.${area}.intro`)} />
      <nav className="ar-tabs" aria-label={t('audit-remediation.actions.nav.label')}>
        <ul>
          {TABS.map((x) => (
            <li key={x.key}><NavLink to={`${x.to}${qs}`} className="ar-tab">{t(`audit-remediation.actions.nav.${x.key}`)}</NavLink></li>
          ))}
        </ul>
      </nav>
      {state === 'loading' ? <Loading label={t('common.loading')} /> : state === 'off' ? <FailureState failure="flag_off" /> : children}
    </section>
  );
}

/** `?job=` query parameter (report deep links pass the audit job id). */
export function useJobParam(): string | null {
  const { search } = useLocation();
  return jobFromSearch(search);
}
export function jobFromSearch(search: string): string | null {
  const v = new URLSearchParams(search).get('job');
  return v && /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,79}$/.test(v) ? v : null;
}
